import "./syncfusion-license";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import App from "./App";
import { store } from "./store";
import { BrowserRouter } from "react-router-dom";
import { PostHogProvider } from "posthog-js/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/clerk-react";
import { TokenProvider } from "./contexts/token";
import "./index.css";

import * as Sentry from "@sentry/react";
import { themeClassName } from "./utils/theme";
import { MaintenancePage } from "./routes/MaintenancePage";

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === "true";

const clerkLocalization = {
  signIn: {
    start: {
      title: "Sign in",
    },
  },
};

// Set dynamic favicon and title based on subdomain
function setDynamicBranding() {
  const hostname = window.location.hostname;

  if (hostname === "carson-mcdowell.liquidacquire.com") {
    // Update favicon
    const link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (link) {
      link.href = "/carson_favicon.ico";
    }

    // Update title
    document.title = "Carson McDowell | Powered by Liquid";
    return;
  }

  if (
    hostname === "itexchangenet.liquidacquire.com" ||
    (import.meta.env.DEV && themeClassName() === "itexchangenet")
  ) {
    const link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (link) {
      link.href = "/itexchangenet_favicon.jpg";
    }

    document.title = import.meta.env.DEV
      ? "IT ExchangeNet (Development)"
      : "IT ExchangeNet";
    return;
  }

  // Make local development unmistakable at a glance.
  if (import.meta.env.DEV) {
    document.title = "Liquid (Development)";
  }
}

setDynamicBranding();

// Get the root element
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

// Create a root
const root = createRoot(rootElement);

const theme = themeClassName();

if (maintenanceMode) {
  root.render(
    <React.StrictMode>
      <div className={theme}>
        <MaintenancePage />
      </div>
    </React.StrictMode>,
  );
} else {
  if (!CLERK_PUBLISHABLE_KEY) {
    throw new Error("Missing Publishable Key");
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    integrations: [],
  });

  const queryClient = new QueryClient();
  const persistor = persistStore(store);

  // Render the app to the root
  root.render(
    <React.StrictMode>
      <div className={theme}>
        <ClerkProvider
          publishableKey={CLERK_PUBLISHABLE_KEY}
          localization={clerkLocalization}
        >
          <PostHogProvider
            apiKey={import.meta.env.VITE_POSTHOG_KEY}
            options={{
              api_host: import.meta.env.VITE_POSTHOG_HOST,
            }}
          >
            <TokenProvider>
              <Provider store={store}>
                <QueryClientProvider client={queryClient}>
                  <PersistGate loading={null} persistor={persistor}>
                    <BrowserRouter>
                      <App />
                    </BrowserRouter>
                  </PersistGate>
                </QueryClientProvider>
              </Provider>
            </TokenProvider>
          </PostHogProvider>
        </ClerkProvider>
      </div>
    </React.StrictMode>,
  );
}
